import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserSecret, faCheck } from "@fortawesome/free-solid-svg-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { useViewAsRole } from "../contexts/ViewAsRoleContext";
import { useRole } from "@x-veloper/dataslot-auth";
import { t } from "@/shared/lib/th-translations";

type RoleType = "SuperAdmin" | "Admin" | "Member";

interface RoleOption {
  value: RoleType;
  label: string;
  description: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: "SuperAdmin",
    label: t("roleSelector.roles.superAdmin.label"),
    description: t("roleSelector.roles.superAdmin.description"),
  },
  {
    value: "Admin",
    label: t("roleSelector.roles.admin.label"),
    description: t("roleSelector.roles.admin.description"),
  },
  {
    value: "Member",
    label: t("roleSelector.roles.member.label"),
    description: t("roleSelector.roles.member.description"),
  },
];

/**
 * RoleSelector Component
 * Allows SuperAdmin and Admin users to preview the application as different roles
 *
 * Features:
 * - Only visible to SuperAdmin and Admin users
 * - Dropdown with available roles (SuperAdmin, Admin, Member)
 * - Shows current effective role
 * - Visual indicator when in preview mode
 */
export function RoleSelector() {
  const actualRole = useRole();
  const { canUsePreview, previewRole, setPreviewRole, isInPreviewMode } = useViewAsRole();

  // Only show for SuperAdmin and Admin users
  if (!canUsePreview) {
    return null;
  }

  const effectiveRole = previewRole || actualRole;

  const getRoleLabel = (role: string) => {
    const option = ROLE_OPTIONS.find((opt) => opt.value === role);
    return option?.label || role;
  };

  const handleRoleSelect = (role: RoleType) => {
    if (role === actualRole) {
      // Return to actual role
      setPreviewRole(null);
    } else {
      // Preview as selected role
      setPreviewRole(role);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isInPreviewMode ? "default" : "outline"}
          size="sm"
          className={isInPreviewMode ? "bg-orange-500 hover:bg-orange-600" : ""}
        >
          <FontAwesomeIcon icon={faUserSecret} className="mr-2 h-4 w-4" />
          {isInPreviewMode && effectiveRole ? `Viewing as ${getRoleLabel(effectiveRole)}` : t("roleSelector.previewAsRole")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Role Preview (Admin Access)
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ROLE_OPTIONS.map((option) => {
          const isSelected = effectiveRole === option.value;
          const isActualRole = actualRole === option.value;

          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleRoleSelect(option.value)}
              className="flex items-start gap-3 py-2 cursor-pointer"
            >
              <div className="flex h-5 w-5 items-center justify-center">
                {isSelected && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="h-4 w-4 text-primary"
                  />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{option.label}</p>
                  {isActualRole && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                      Your Role
                    </span>
                  )}
                  {isSelected && !isActualRole && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                      Preview
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {option.description}
                </p>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
